import React, { useState, useEffect } from 'react';
import axiosInstance from '../../axiosInstance';
import { CircularProgress } from '@mui/material';
import Filter from './Filter';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { Modal } from '@mui/material';

export default function MobileProduct() {
    // All hooks at the top
    const { data: user } = useAuth();
    const [testModeModal, setTestModeModal] = useState(false);
    const [activeTab, setActiveTab] = useState("all");
    const [products, setProducts] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFilterOpen, setFilterOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [filters, setFilters] = useState({
      category: '',
      minPrice: 0,
      maxPrice: 10000000,
      search: ''
    });
    const [highlightFilter, setHighlightFilter] = useState(false);
    const [myProducts, setMyProducts] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
      if (user && !user.onboarded) setTestModeModal(true);
    }, [user]);

    useEffect(() => {
      if (filters.category && filters.category !== 'all') {
        fetchProducts();
      }
    }, [filters.category]);

    useEffect(() => {
      applyFilters();
    }, [products, filters.minPrice, filters.maxPrice, filters.search]);

    useEffect(() => {
      setCategoriesLoading(true);
      axiosInstance.get('/product-category')
        .then(res => {
          const categoryData = res.data?.data?.data;
          if (Array.isArray(categoryData)) {
            setCategories(categoryData);
          } else {
            setCategories([]);
          }
        })
        .catch(() => setCategories([]))
        .finally(() => setCategoriesLoading(false));
    }, []);

    useEffect(() => {
      setHighlightFilter(!filters.category);
    }, [filters.category]);

    useEffect(() => {
      if (activeTab !== 'my') return;
      setIsLoading(true);
      axiosInstance.get('/shop/product/list')
        .then(res => {
          let fetchedProducts = [];
          if (res.data.data) {
            if (Array.isArray(res.data.data)) {
              fetchedProducts = res.data.data;
            } else if (res.data.data.data) {
              fetchedProducts = res.data.data.data;
            }
          }
          setMyProducts(fetchedProducts);
        })
        .catch(() => {
          setMyProducts([]);
        })
        .finally(() => setIsLoading(false));
    }, [activeTab]);

    const fetchProducts = async () => {
      setIsLoading(true);
      const endpoint = filters.category === 'all' 
        ? '/shop/product/list'
        : `/shop/product/${filters.category}`;

      axiosInstance.get(endpoint)
        .then(res => {
          console.log('API Response:', res.data);
          let fetchedProducts = [];
          if (res.data.data) {
            if (Array.isArray(res.data.data)) {
              fetchedProducts = res.data.data;
            } else if (res.data.data.data) {
              fetchedProducts = res.data.data.data;
            }
          }
          setProducts(fetchedProducts);
          setAllProducts(fetchedProducts);
        })
        .catch(err => {
          console.error("Failed to fetch products", err);
          if (err.response?.status === 401) {
            toast.error('Session expired. Please login again.');
          }
        })
        .finally(() => setIsLoading(false));
    };

    const applyFilters = () => {
      let filtered = [...allProducts];

      if (filters.search) {
        filtered = filtered.filter(p => 
          p.product_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
          p.product_id?.product_name?.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      if (filters.minPrice || filters.maxPrice) {
        filtered = filtered.filter(p => {
          const price = p.price || p.product_id?.price || 0;
          return price >= filters.minPrice && price <= filters.maxPrice;
        });
      }

      setProducts(filtered);
    };

    const handleSearchChange = (e) => {
      setFilters(prev => ({ ...prev, search: e.target.value }));
    };

    const handleQuantityChange = (index, type) => {
        setProducts(currentProducts =>
            currentProducts.map((product, idx) =>
                idx === index ? {
                    ...product,
                    quantity: type === "inc" ? product.quantity + 1 : product.quantity > 1 ? product.quantity - 1 : 1,
                } :
                product
            )
        );
    };

    const switchTab = (tab) => {
        setIsLoading(true);
        setTimeout(() => {
            setActiveTab(tab);
            setIsLoading(false);
        }, 200);
    };
    
    const handleApplyFilters = (newFilters) => {
      setFilters(prev => ({ ...prev, ...newFilters }));
      setFilterOpen(false);
    };

    const handleViewClick = (categoryId) => {
        navigate(`/app/products/${categoryId}`);
    };

    // After all hooks, do the conditional return
    if (user && !user.onboarded) {
      return (
        <Modal open={testModeModal} onClose={() => {}}>
          <div style={{
            background: 'white',
            borderRadius: 20,
            maxWidth: 350,
            margin: '100px auto',
            padding: 32,
            boxShadow: '0 4px 32px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            outline: 'none',
          }}>
            <div style={{ fontSize: 48, color: '#f79009', marginBottom: 16 }}>⚠️</div>
            <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0, marginBottom: 8, textAlign: 'center' }}>Currently on Test Mode</h2>
            <p style={{ color: '#475467', fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
              Complete your profile to perform other transactions
            </p>
            <div style={{ display: 'flex', gap: 16, width: '100%' }}>
              <button
                style={{
                  flex: 1,
                  background: 'white',
                  color: '#af3585',
                  border: '2px solid #af3585',
                  borderRadius: 8,
                  padding: '10px 8px',
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: 'pointer',
                  minWidth: 0,
                  marginRight: 4
                }}
                onClick={() => navigate('/app/dashboard')}
              >
                Cancel
              </button>
              <button
                style={{
                  flex: 1,
                  background: '#af3585',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 7px',
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: 'pointer',
                  marginLeft: 4
                }}
                onClick={() => navigate('/app/profile?edit=1')}
              >
                Complete Profile
              </button>
            </div>
          </div>
        </Modal>
      );
    }

    // Awaiting Approval modal logic
    if (user && user.onboarded && (user.is_approved !== 'verified' || !user.is_verified)) {
      return (
        <Modal open={true} onClose={() => {}}>
          <div style={{
            background: 'white',
            borderRadius: 20,
            maxWidth: 350,
            margin: '100px auto',
            padding: 32,
            boxShadow: '0 4px 32px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            outline: 'none',
          }}>
            <div style={{ fontSize: 48, color: '#f79009', marginBottom: 16 }}>⚠️</div>
            <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0, marginBottom: 8, textAlign: 'center' }}>Awaiting Verification</h2>
            <p style={{ color: '#475467', fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
              Your profile is currently being verified. This will take at least 24 hours.
            </p>
            <div style={{ display: 'flex', gap: 16, width: '100%' }}>
              <button
                style={{
                  flex: 1,
                  background: 'white',
                  color: '#af3585',
                  border: '2px solid #af3585',
                  borderRadius: 8,
                  padding: '10px 8px',
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: 'pointer',
                  minWidth: 0,
                  marginRight: 4
                }}
                onClick={() => navigate('/app/dashboard')}
              >
                Cancel
              </button>
              <button
                style={{
                  flex: 1,
                  background: '#af3585',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 7px',
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: 'pointer',
                  marginLeft: 4
                }}
                onClick={() => navigate('/app/dashboard')}
              >
                Back Home
              </button>
            </div>
          </div>
        </Modal>
      );
    }

    return (
        <div style={{ background: "white", minHeight: "100vh", padding: 0 }}>
            <div style={{
                display: "flex",
                alignItems: "center",
                padding: "0 0 10px 16px"
            }} />
            <div style={{
                padding: "2px 0 0 18px"
            }}>
                <div style={{
                    fontWeight: 700,
                    fontSize: 23,
                    color: "#222"
                }}>
                    Product List
                </div>
                <div style={{
                    color: "#757575",
                    fontSize: 14,
                    marginTop: 6,
                    maxWidth: 270,
                    marginBottom: 10
                }}>
                    Select from the list product to update your inventory
                </div>
            </div>
            <div style={{
                display: "flex",
                alignItems: "center",
                margin: "18px 16px 0 16px"
            }}>
                <div style={{
                    flex: 1,
                    background: "#fff",
                    border: "1.5px solid #E0E0E0",
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 12px",
                    height: 44
                }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="#757575" strokeWidth="2" />
                        <path d="M20 20l-3-3" stroke="#757575" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <input
                        placeholder="Search"
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        style={{
                            border: "none",
                            outline: "none",
                            fontSize: 17,
                            marginLeft: 10,
                            width: "100%",
                            color: "#757575",
                            background: "transparent"
                        }} />
                </div>
                <div 
                    onClick={() => setFilterOpen(true)}
                    style={{
                        width: 38,
                        height: 38,
                        background: "#fff",
                        border: "1.5px solid #E0E0E0",
                        borderRadius: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: 12,
                        cursor: "pointer",
                        boxShadow: highlightFilter ? '0 0 0 4px #f3e0f3, 0 0 12px 2px #A0207A' : undefined,
                        animation: highlightFilter ? 'pulse 1.2s infinite' : undefined,
                        position: 'relative',
                        zIndex: 2
                    }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M4 6h16M7 12h10M10 18h4" stroke="#222" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {highlightFilter && (
                      <div style={{
                        position: 'absolute',
                        top: -38,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#fff',
                        color: '#A0207A',
                        padding: '4px 12px',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        boxShadow: '0 2px 8px rgba(160,32,122,0.12)',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        zIndex: 3,
                        border: '1.5px solid #A0207A',
                      }}>
                        Click here!
                      </div>
                    )}
                    <style>{`
                      @keyframes pulse {
                        0% { box-shadow: 0 0 0 0 #A0207A44; }
                        70% { box-shadow: 0 0 0 8px #A0207A11; }
                        100% { box-shadow: 0 0 0 0 #A0207A44; }
                      }
                    `}</style>
                </div>
            </div>
            <div style={{
                display: "flex",
                alignItems: "center",
                marginTop: 30,
                marginLeft: 24
            }}>
                <div style={{
                    fontWeight: 500,
                    fontSize: 17,
                    color: activeTab === "all" ? "#A0207A" : "#BDBDBD",
                    borderBottom: activeTab === "all" ? "2.5px solid #A0207A" : "none",
                    paddingBottom: 2,
                    marginRight: 18,
                    cursor: "pointer"
                }} onClick={() => switchTab("all")}>
                    All Product
                </div>
                <div style={{
                    fontWeight: 500,
                    fontSize: 17,
                    color: activeTab === "my" ? "#A0207A" : "#BDBDBD",
                    borderBottom: activeTab === "my" ? "2.5px solid #A0207A" : "none",
                    paddingBottom: 2,
                    cursor: "pointer"
                }} onClick={() => switchTab("my")}>
                    My Product
                </div>
            </div>
            <div style={{
                marginTop: 10,
                transition: "opacity 0.3s",
                opacity: isLoading ? 0.5 : 1,
                position: 'relative'
            }}>
                {isLoading && (
                    <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                        <CircularProgress style={{ color: '#A0207A' }} size={40} thickness={4} />
                    </div>
                )}
                {!filters.category && !isLoading && activeTab === "all" && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: 60,
                  }}>
                    <div style={{
                      background: '#fff',
                      borderRadius: 16,
                      boxShadow: '0 4px 24px rgba(160,32,122,0.08)',
                      padding: '32px 24px',
                      maxWidth: 320,
                      textAlign: 'center',
                      position: 'relative',
                    }}>
                      <div style={{ color: '#757575', fontSize: 16, marginBottom: 18 }}>
                        Tap the <b>filter</b> button above to choose a category and start browsing products.
                      </div>
                    </div>
                  </div>
                )}
                {((activeTab === "all" && filters.category && products.length === 0) || (activeTab === "my" && myProducts.length === 0 && !isLoading)) && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: 60,
                  }}>
                    <div style={{
                      background: '#fff',
                      borderRadius: 16,
                      boxShadow: '0 4px 24px rgba(160,32,122,0.08)',
                      padding: '32px 24px',
                      maxWidth: 320,
                      textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#A0207A', marginBottom: 8 }}>
                        No Products
                      </div>
                      <div style={{ color: '#757575', fontSize: 16 }}>
                        No products found for the selected tab. Try another!
                      </div>
                    </div>
                  </div>
                )}
                {((activeTab === "all" && filters.category && products.length > 0) || (activeTab === "my" && myProducts.length > 0)) && (
                  <>
                    {(activeTab === "all" ? products : myProducts).map((product, index) => (
                        <div key={index} style={{
                            background: "#fff",
                            borderRadius: 18,
                            margin: "0 16px 18px 16px",
                            padding: 0,
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 100,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.01)"
                        }}>
                            {activeTab === "all" ? (
                                <>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        padding: "16px 16px 0 16px"
                                    }}>
                                        <img src={product.product_image} alt="product" style={{
                                            width: 44,
                                            height: 62,
                                            objectFit: "contain",
                                            borderRadius: 6,
                                            marginRight: 14
                                        }} />
                                        <div style={{
                                            flex: 1
                                        }}>
                                            <div style={{
                                                color: "#757575",
                                                fontSize: 14,
                                                fontWeight: 500
                                            }}>
                                                Item Name
                                            </div>
                                            <div style={{
                                                color: "#222",
                                                fontWeight: 500,
                                                fontSize: 16,
                                                lineHeight: 1.4
                                            }}>
                                                {product.product_name}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        padding: "0 16px 16px 16px"
                                    }}>
                                        <button style={{
                                            width: "100%",
                                            height: 38,
                                            border: "1.5px solid #A0207A",
                                            borderRadius: 8,
                                            background: "#fff",
                                            color: "#A0207A",
                                            fontWeight: 500,
                                            fontSize: 16,
                                            marginTop: 12,
                                            cursor: "pointer"
                                        }}>
                                            Add to cart
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        padding: "16px 16px 0 16px",
                                        position: "relative"
                                    }}>
                                        <img src={product.product_id?.product_image} alt="product" style={{
                                            width: 44,
                                            height: 62,
                                            objectFit: "contain",
                                            borderRadius: 6,
                                            marginRight: 14
                                        }} />
                                        <div style={{
                                            flex: 1
                                        }}>
                                            <div style={{
                                                color: "#757575",
                                                fontSize: 14,
                                                fontWeight: 500
                                            }}>
                                                Item Name
                                            </div>
                                            <div style={{
                                                color: "#222",
                                                fontWeight: 500,
                                                fontSize: 16,
                                                lineHeight: 1.4
                                            }}>
                                                {product.product_id?.product_name}
                                            </div>
                                        </div>
                                        <div style={{
                                            position: "absolute",
                                            right: 16,
                                            top: 16,
                                            display: "flex",
                                            alignItems: "center"
                                        }}>
                                            <span style={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                background: "#1DC100",
                                                display: "inline-block",
                                                marginRight: 4
                                            }} />
                                            <span style={{
                                                color: "#1DC100",
                                                fontSize: 14,
                                                fontWeight: 500,
                                                whiteSpace: "nowrap"
                                            }}>
                                                In Stock
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "12px 16px 16px 16px"
                                    }}>
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center"
                                        }}>
                                            <button onClick={() => handleQuantityChange(index, "dec")} style={{
                                                width: 32,
                                                height: 32,
                                                border: "2px solid #E0E0E0",
                                                borderRadius: 8,
                                                background: "#fff",
                                                color: "#222",
                                                fontWeight: 600,
                                                fontSize: 20,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: "pointer"
                                            }}>
                                                -
                                            </button>
                                            <div style={{
                                                margin: "0 12px",
                                                width: 32,
                                                height: 32,
                                                border: "2px solid #E0E0E0",
                                                borderRadius: 8,
                                                background: "#fff",
                                                color: "#222",
                                                fontWeight: 600,
                                                fontSize: 16,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}>
                                                {product.quantity}
                                            </div>
                                            <button onClick={() => handleQuantityChange(index, "inc")} style={{
                                                width: 32,
                                                height: 32,
                                                border: "2px solid #A0207A",
                                                borderRadius: 8,
                                                background: "#fff",
                                                color: "#A0207A",
                                                fontWeight: 600,
                                                fontSize: 20,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: "pointer"
                                            }}>
                                                +
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                  </>
                )}
            </div>

            <Filter 
                isOpen={isFilterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleApplyFilters}
                categories={categories}
                loading={categoriesLoading}
            />
        </div>
    );
} 